import React, { useEffect, useState } from 'react';
import { Theme, styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import SyncIcon from '@mui/icons-material/Sync';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';

import {
    useManualySync,
    useGetConnectorSyncInfo,
} from './useGetConnectorSyncInfo';
import { useIdentityContext } from '../components/identity';
import { useNotify } from 'react-admin';

import moment from 'moment';
import { MenuItem, useMediaQuery } from '@mui/material';
import Fade from '@mui/material/Fade';
import { NUMBER } from '../utils/Constants/MagicNumber';

const StyledSyncButton = styled(Button)({
    '&.MuiButton-outlined': {
        border: '1px solid #FC6E45',
        borderRadius: 4,
        backgroundColor: '#FEEFEA',
        minWidth: '185px',
    },
});

const StyledSynchronizingButton = styled(Button)({
    '&.MuiButton-outlined': {
        border: '1px solid #D2D2D2',
        borderRadius: 4,
        backgroundColor: '#F4F4F4',
        minWidth: '185px',
    },
});

const SyncTime = ({ time }) => {
    const sync_date = moment.utc(time).local().format('MMM DD, YYYY | h:mm A');
    return (
        <Typography
            color="#8A8A8A"
            style={{
                fontSize: 12,
                fontWeight: 400,
                fontFamily: 'Poppins',
            }}
        >
            {sync_date}
        </Typography>
    );
};

const SyncButton = ({
    connector,
    title = '',
    isSmall = false,
    onClick = null,
    ...props
}) => {
    const { manualySync, isLoading: isLoadingManualySync } = useManualySync();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const handleSync = () => {
        setLoading(true);
        manualySync(connector, {
            onSuccess: connection => {
                setLoading(false);
                notify('Sync is started');
                if (onClick) {
                    onClick();
                }
            },
            onError: error => {
                setLoading(false);
                notify(`Sync Failure ! ${error.message}`);
                if (onClick) {
                    onClick();
                }
            },
        });
    };

    if (loading) {
        return (
            <StyledSynchronizingButton
                variant="outlined"
                className='sync-button-responsive'
                startIcon={
                    !isSmall ? (
                        <MoreHorizIcon
                            style={{ width: 24, height: 24, color: '#979797' }}
                        />
                    ) : null
                }
                {...props}
            >
                {!isSmall ? (
                    <Stack direction="column" alignItems={'flex-start'}>
                        <Typography
                            className="sync-title"
                            color="#979797"
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                fontFamily: 'Poppins',
                            }}
                        >
                            Synchronizing
                        </Typography>
                    </Stack>
                ) : (
                    <MoreHorizIcon
                        style={{ width: 24, height: 24, color: '#979797' }}
                    />
                )}
            </StyledSynchronizingButton>
        );
    }
    return (
        <StyledSyncButton
            variant="outlined"
            className='sync-button-responsive'
            startIcon={
                !isSmall ? <SyncIcon style={{ width: 24, height: 24 }} /> : null
            }
            onClick={handleSync}
            {...props}
        >
            {isSmall ? (
                <SyncIcon style={{ width: 24, height: 24 }} />
            ) : (
                <Stack direction="column" alignItems={'flex-start'}>
                    <SyncTitle text={title || 'Sync Data'}></SyncTitle>
                    {connector?.last_sync_time && (
                        <SyncTime time={connector.last_sync_time} />
                    )}
                </Stack>
            )}
        </StyledSyncButton>
    );
};

const SyncTitle = ({ text }) => {
    return (
        <Typography
            color="#FC6E45"
            style={{
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'Poppins',
            }}
        >
            {text}
        </Typography>
    );
};
const Sync = ({ company_id }) => {
    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
    } = useGetConnectorSyncInfo(company_id);
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if(data?.connectors?.length){
            document.body.classList.remove("userflow-sync");
        }else if(data?.connectors?.length === NUMBER.ZERO){
            document.body.classList.add("userflow-sync");
        }
    }, [data?.connectors?.length])

    if (isLoading || !data) {
        return null;
    }
    const connectors = data.connectors;

    if (connectors.length == 0) {
        return null;
    } else if (connectors.length == 1) {
        return <SyncButton connector={connectors[0]} isSmall={isSmall} />;
    } else if (connectors.length > 1) {
        return (
            <div>
                <StyledSyncButton
                    id="fade-button"
                    className='sync-button-responsive'
                    aria-controls={open ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    startIcon={
                        !isSmall ? (
                            <SyncIcon style={{ width: 24, height: 24 }} />
                        ) : null
                    }
                    endIcon={
                        !isSmall ? (
                            <KeyboardArrowDownIcon
                                style={{ width: 24, height: 24 }}
                            />
                        ) : null
                    }
                >
                    {!isSmall ? (
                        <Stack direction="column" alignItems={'flex-start'}>
                            <SyncTitle text="Sync Data"></SyncTitle>
                        </Stack>
                    ) : (
                        <KeyboardArrowDownIcon
                            style={{ width: 24, height: 24 }}
                        />
                    )}
                </StyledSyncButton>
                <Menu
                    id="fade-menu"
                    MenuListProps={{
                        'aria-labelledby': 'fade-button',
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                >
                    {connectors.map(connector => {
                        return (
                            <MenuItem>
                                <SyncButton
                                    title={connector.name}
                                    connector={connector}
                                    onClick={handleClose}
                                />
                            </MenuItem>
                        );
                    })}
                </Menu>
            </div>
        );
    }
    return null;
};

const SyncComponent = () => {
    const identity = useIdentityContext();

    if (!identity || !identity?.company_id) {
        return null;
    }

    return <Sync company_id={identity?.company_id} />;
};

export default SyncComponent;
